import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Breadcrumbs from "examples/Breadcrumbs";
import routes from "routes";
import { useAuth } from "auth/AuthContext";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDarkMode,
} from "context";

function routeName(key) {
  return routes.find((r) => r.type === "collapse" && r.key === key)?.name;
}

function DashboardNavbar({ absolute, light, isMini, title }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [accountMenu, setAccountMenu] = useState(null);
  const { admin, logout } = useAuth();
  const segments = useLocation().pathname.split("/").slice(1).filter(Boolean);
  const crumbs = segments.map((segment, index) => ({
    path: `/${segments.slice(0, index + 1).join("/")}`,
    name: index === 0 ? routeName(segment) || segment : `#${segment}`,
  }));

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon="home"
            crumbs={crumbs}
            title={title || crumbs[crumbs.length - 1]?.name || "Tổng quan"}
            light={light}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center">
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <Tooltip title={darkMode ? "Giao diện sáng" : "Giao diện tối"}>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={() => setDarkMode(dispatch, !darkMode)}
                >
                  <Icon sx={iconsStyle}>{darkMode ? "light_mode" : "dark_mode"}</Icon>
                </IconButton>
              </Tooltip>
              <Tooltip title="Tuỳ chỉnh giao diện">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={() => setOpenConfigurator(dispatch, !openConfigurator)}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={(event) => setAccountMenu(event.currentTarget)}
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
                {admin && (
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    ml={0.5}
                    color={light ? "white" : "dark"}
                  >
                    {admin.name}
                  </MDTypography>
                )}
              </IconButton>
              <Menu
                anchorEl={accountMenu}
                open={Boolean(accountMenu)}
                onClose={() => setAccountMenu(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ mt: 1 }}
              >
                {admin && (
                  <MenuItem disabled>
                    <MDTypography variant="caption">
                      Tài khoản: {admin.username} (id {admin.userId})
                    </MDTypography>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setAccountMenu(null);
                    logout();
                  }}
                >
                  <ListItemIcon>
                    <Icon fontSize="small">logout</Icon>
                  </ListItemIcon>
                  Đăng xuất
                </MenuItem>
              </Menu>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  title: null,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  title: PropTypes.string,
};

export default DashboardNavbar;
